import { isRedealFrame, type ShelemView } from "@bing/engine-shelem";
import { useEventSfx } from "./use-table-sfx";

/* ------------------------------------------------------------------ *
 * شلم's AUCTION CUES (P1 Task 4, R5) — `bid`, `pass` and `shelemCall` for the OTHER seats' moves,
 * the way `useCardLandSfx` clicks for another seat's card: your own tap sounds at once in the row
 * (the table's button handlers), so the frame that echoes it is silent for you.
 *
 * Each cue is keyed by a STRING read off the frame (`useEventSfx`: fires when the string changes to
 * a new NON-EMPTY value, never at mount), so a socket-frame effect depends on primitives only:
 *   bid   «seat:amount» for every OTHER seat with a standing bid — a raise changes it; a call takes
 *         the caller's 165 OUT of it (the call has its own cue, never «a 165 bid» as well);
 *   pass  the other seats that have passed, in seat order — on the REDEAL's frame (D6), the three
 *         whose passes dealt it (`passedOut`): no frame carried the third, so that frame is where
 *         it is heard (the final review B-I2);
 *   call  the caller's seat, while the call stands (`called`), unless the caller is you.
 * The next hand's deal empties every string: silence by construction. The redeal's shuffle is the
 * table's (`useRedealMoments`, with its notice): a 12 → 12 deal never sounds `useDealSfx`'s. A resume
 * mid-auction adopts the frame in silence.
 * ------------------------------------------------------------------ */

export interface AuctionSfxSigs {
  readonly bid: string;
  readonly pass: string;
  readonly call: string;
}

/** What `view` says about the other seats' auction, as three strings (see above). Pure. */
export function auctionSfxSigs(view: ShelemView, you: number): AuctionSfxSigs {
  const live = view.phase === "bidding" || view.phase === "declaring";
  if (!live) return { bid: "", pass: "", call: "" };
  const caller = view.called ? view.declarer : null;
  const bid = view.bids
    .map((amount, seat) =>
      amount !== null && seat !== you && seat !== caller ? `${seat}:${amount}` : null,
    )
    .filter((x): x is string => x !== null)
    .join(",");
  const passers = isRedealFrame(view)
    ? (view.passedOut ?? [])
    : view.passed.flatMap((p, seat) => (p ? [seat] : []));
  const pass = passers
    .filter((seat) => seat !== you)
    .map(String)
    .join(",");
  const call = caller !== null && caller !== you ? `${caller}` : "";
  return { bid, pass, call };
}

/** Play the other seats' bids, passes and call as the frames land (see above). */
export function useAuctionSfx(view: ShelemView, you: number): void {
  const sigs = auctionSfxSigs(view, you);
  useEventSfx(sigs.bid, "bid");
  useEventSfx(sigs.pass, "pass");
  useEventSfx(sigs.call, "shelemCall");
}
